import React, { useEffect, useState } from "react";
import NewNavbar from "./Home/NewNavbar";
import NewFooter from "./Home/NewFooter";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useJobs } from "./context/JobContext";
import { uploadFileToS3 } from "./utils/s3Uploader";
import ContentBlocks from "./ContentBlocks";
import {
  trackJobApplicationStart,
  trackJobApplicationSuccess,
} from "./utils/analytics";
import { ADMIN_API_BASE_URL } from "./api/apiBaseUrl";

function JobDetail() {
  const { jobListings, submitApplication } = useJobs();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    terms: false,

    qualification: [],
    workExp: "",
    isFresher: false,
    currentCtc: "",
    expectedCtc: "",

    noticePeriod: "",

    resumeFile: null, // File object
    resumeUrl: "", // S3 URL
    resumeLoading: false, // show loader

    portfolioFile: null, // File object for PDF
    portfolioFileUrl: "", // S3 URL
    portfolioLink: "", // link instead of PDF
    portfolioLoading: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const uploadToS3 = async (file, form) => {
    if (!file) throw new Error("No file provided");

    if (!form.name?.trim()) return alert("Name is required before uploading");
    if (!form.phone?.trim()) return alert("Phone is required before uploading");

    const fileWithKeyPrefix = new File(
      [file],
      `${form.name}-${form.phone}-${job.jobId}-${file.name}`,
      {
        type: file.type,
      }
    );
    console.log(fileWithKeyPrefix);

    // --- Upload using real S3 uploader ---
    const { url } = await uploadFileToS3({
      file: fileWithKeyPrefix,
      contentType: file.type,
    });
    return url;
  };

  const handleCheckbox = (e) => {
    const { name, checked } = e.target;

    // Special logic for “I'm a fresher”
    if (name === "isFresher") {
      setForm((prev) => ({
        ...prev,
        isFresher: checked,
        workExp: checked ? "0" : prev.workExp,
        currentCtc: checked ? "" : prev.currentCtc,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const toggleQualification = (value) => {
    setForm((prev) => {
      const alreadySelected = prev.qualification.includes(value);

      return {
        ...prev,
        qualification: alreadySelected
          ? prev.qualification.filter((q) => q !== value)
          : [...prev.qualification, value],
      };
    });
  };
  const handleNoticePeriod = (value) => {
    setForm((prev) => ({
      ...prev,
      noticePeriod: prev.noticePeriod === value ? "" : value,
    }));
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Only PDF allowed");
      return;
    }

    setForm((prev) => ({ ...prev, resumeLoading: true }));

    const url = await uploadToS3(file, form);

    setForm((prev) => ({
      ...prev,
      resumeFile: file,
      resumeUrl: url,
      resumeLoading: false,
    }));
  };

  const handlePortfolioPDF = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Only PDF allowed");
      return;
    }

    setForm((prev) => ({ ...prev, portfolioLoading: true }));

    const url = await uploadToS3(file, form);

    setForm((prev) => ({
      ...prev,
      portfolioFile: file,
      portfolioFileUrl: url,
      portfolioLink: "", // clear link if PDF chosen
      portfolioLoading: false,
    }));
  };

  const handlePortfolioLink = (e) => {
    const value = e.target.value;

    setForm((prev) => ({
      ...prev,
      portfolioLink: value,
      portfolioFile: null,
      portfolioFileUrl: "",
    }));
  };

  const reuploadResume = () => {
    setForm((prev) => ({
      ...prev,
      resumeFile: null,
      resumeUrl: "",
    }));
  };

  const reuploadPortfolio = () => {
    setForm((prev) => ({
      ...prev,
      portfolioFile: null,
      portfolioFileUrl: "",
      portfolioLink: "",
    }));
  };

  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(1);
  const [isEnabled, setIsEnabled] = useState(false);

  const [job, setJob] = useState(null);

  useEffect(() => {
    const localJob = jobListings.find((j) => String(j.jobId) === String(jobId));

    if (localJob) {
      setJob(localJob);
      setLoading(false);
      return;
    }

    // If not found locally → fetch from API
    const fetchJob = async () => {
      try {
        const res = await fetch(
          `${ADMIN_API_BASE_URL}/careers/job/${jobId}`
        );

        if (!res.ok) {
          navigate("/", { replace: true });
          return;
        }

        const data = await res.json();
        setJob(data.data);
      } catch (err) {
        console.error("Failed to fetch job:", err);
        navigate("/", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [jobId, navigate]);

  const validateForm = (form) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    // REQUIRED FIELDS
    if (!form.name.trim()) return false;
    if (!form.email.trim() || !emailRegex.test(form.email)) return false;
    if (!form.phone.trim() || !phoneRegex.test(form.phone)) return false;

    // QUALIFICATION
    if (form.qualification.length === 0) return false;

    // WORK EXPERIENCE
    if (!form.isFresher && !form.workExp) return false;

    // CTC
    if (!form.expectedCtc.trim()) return false;
    if (!form.isFresher && !form.currentCtc.trim()) return false;

    // RESUME (mandatory)
    if (!form.resumeUrl) return false;

    // NOTICE PERIOD
    if (!form.noticePeriod) return false;

    // TERMS & CONDITIONS
    if (!form.terms) return false;

    return true; // All checks passed
  };

  useEffect(() => {
    setIsEnabled(validateForm(form));
  }, [form]);

  if (!job) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log(form);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    // Required fields
    if (!form.name.trim()) return alert("Name is required");

    if (!form.email.trim()) return alert("Email is required");
    if (!emailRegex.test(form.email.trim()))
      return alert("Enter a valid email address");

    if (!form.phone.trim()) return alert("Phone number is required");
    if (!phoneRegex.test(form.phone.trim()))
      return alert("Phone number must be exactly 10 digits");

    // Qualification
    if (form.qualification.length === 0)
      return alert("Select at least one qualification");

    // Work experience
    if (!form.isFresher && !form.workExp)
      return alert("Work experience is required");

    // CTC
    if (!form.expectedCtc.trim())
      return alert("Expected CTC is required");
    if (!form.isFresher && !form.currentCtc.trim())
      return alert("Current CTC is required");

    // Resume required
    if (!form.resumeUrl) return alert("Please upload your resume (PDF only).");

    // Notice period (if any)
    if (!form.noticePeriod) return alert("Please select a notice period.");

    // Terms checkbox
    if (!form.terms) return alert("Please accept the terms and condition.");

    trackJobApplicationStart({ buttonName: "Submit Application" });

    const hasPDF = !!form.portfolioFileUrl;
    const hasLink = !!form.portfolioLink.trim();

    if (hasPDF && hasLink) {
      return alert("Choose only ONE portfolio option: PDF OR Link.");
    }

    let finalPortfolio = "";

    if (hasPDF) finalPortfolio = form.portfolioFileUrl;
    else if (hasLink) finalPortfolio = form.portfolioLink;
    else finalPortfolio = ""; // acceptable if optional

    const payload = {
      jobId: job?.jobId,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),

      terms: form.terms,
      qualification: form.qualification,
      isFresher: form.isFresher,
      workExp: form.isFresher ? "0" : form.workExp,
      currentCtc: form.isFresher ? "" : form.currentCtc.trim(),
      expectedCtc: form.expectedCtc.trim(),

      noticePeriod: form.noticePeriod,

      resumeUrl: form.resumeUrl,
      portfolioFileUrl: form.portfolioFileUrl,
      portfolioLink: form.portfolioLink,
      appliedAt: new Date().toISOString(),
      jobId,
    };

    try {
      setLoading(true);

      const res = await submitApplication(job?.jobId, payload);

      if (res?.success) {
        trackJobApplicationSuccess({ buttonName: "Submit Application" });
        setState(3);
      } else {
        alert(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to submit application.");
    } finally {
      setLoading(false);
    }
  };

  const renderQualificationBtn = (label) => {
    const isSelected = form.qualification.includes(label);

    return (
      <button
        key={label}
        type="button"
        onClick={() => toggleQualification(label)}
        className={`flex items-center gap-2 px-4 h-[40px] rounded-[20px] text-[14px] font-nunito
          ${
            isSelected
              ? "bg-black text-white"
              : "border border-[#D9D9D9] text-black"
          }
        `}
      >
        <img src={isSelected ? "/crossOption.svg" : "/addOption.svg"} alt="" />
        {label}
      </button>
    );
  };

  const renderNoticeBtn = (label) => {
    const isSelected = form.noticePeriod === label;

    return (
      <button
        key={label}
        type="button"
        onClick={() => handleNoticePeriod(label)}
        className={`flex items-center gap-2 px-4 h-[40px] rounded-[20px] text-[14px] font-nunito
        ${
          isSelected
            ? "bg-black text-white"
            : "border border-[#D9D9D9] text-black"
        }
      `}
      >
        <img src={isSelected ? "/crossOption.svg" : "/addOption.svg"} alt="" />
        {label}
      </button>
    );
  };

  return (
    <div className="relative min-h-screen bg-white flex flex-col">
      <div className="relative z-[100] md:border-b">
        <NewNavbar />
      </div>

      <div className="absolute top-0 left-0 w-full h-full z-0 overflow-hidden pointer-events-none">
        {/* Top Gradient Row */}
        <div className="absolute top-[-250px] left-1/2 -translate-x-1/2 w-[1400px] flex justify-between opacity-70">
          <div className="w-[612.5px] h-[612.5px] bg-[radial-gradient(50%_50%_at_50%_50%,rgba(39,196,133,0.24)_0%,rgba(233,242,252,0)_100%)]"></div>

          <div className="w-[612.5px] h-[612.5px] bg-[radial-gradient(50%_50%_at_50%_50%,#EBFCEA_0%,rgba(234,252,236,0)_100%)]"></div>
        </div>

        {/* Bottom Gradient Row */}
        <div className="absolute top-[300px] left-1/2 -translate-x-1/2 w-[1400px] flex justify-between opacity-40">
          <div className="w-[612.5px] h-[612.5px] bg-[radial-gradient(50%_50%_at_50%_50%,#E9F2FC_0%,rgba(233,242,252,0)_100%)]"></div>

          <div className="w-[612.5px] h-[612.5px] bg-[radial-gradient(50%_50%_at_50%_50%,#EBFCEA_0%,rgba(234,252,236,0)_100%)]"></div>
        </div>
      </div>
      <div
        className={`
            flex-1 bg-white z-10 px-5 mx-auto md:mt-[120px] w-full pb-10
            ${state === 1 ? "md:max-w-[968px]" : "md:max-w-[1088px]"}
          `}
      >
        {state === 1 && (
          <>
            <div className="w-full flex md:hidden md:flex-col flex-col-reverse items-center justify-center md:mt-20 gap-3 md:gap-6">
              <h2 className="md:text-center ml-10 md:ml-0 w-full max-w-[685px] h-8 md:text-[20px] text-[16px] text-[#656565] font-nunito font-bold leading-[32px]">
                Careers at HYNT
              </h2>

              <p className="md:text-center w-full px-4 font-oran font-normal md:text-[64px] text-[40px] leading-[64px]">
                Open positions
              </p>
            </div>
            <div className="h-0 block md:hidden border-t border-[#E0E0E0] border-dashed border-[1px] [stroke-dasharray:2_2]"></div>
          </>
        )}
        <div className="h-[54px] w-full flex items-center ">
          {state === 2 || state === 3 ? (
            <p
              onClick={() => setState(1)}
              className="flex items-center font-nunito font-medium text-[16px] tracking-tighter leading-[0.41px] cursor-default"
            >
              <img src="/CaretRight.svg" alt="" className="mr-1" /> Back to Job
              details
            </p>
          ) : (
            <Link to="/careers">
              <p className="flex items-center font-nunito font-medium text-[16px] tracking-tighter leading-[0.41px]">
                <img src="/CaretRight.svg" alt="" className="mr-1" /> Back to
                all jobs
              </p>
            </Link>
          )}
        </div>
        <div className="w-full flex  flex-col gap-[8px] mt-[24px]">
          <div className="w-full px-4 py-2 flex flex-col md:gap-2 gap-6 md:h-[66px] md:flex-row md:items-center md:justify-between">
            {/* LEFT SIDE */}
            <div className="flex flex-col leading-tight">
              {/* Title */}
              <h2 className="font-nunito font-bold text-[16px] text-black">
                {job.title}
              </h2>

              {/* Meta Row */}
              <div className="flex items-center md:gap-3 gap-1 text-[#6F6F6F] font-nunito md:text-[14px] text-[13px] mt-1">
                <p>Job ID: {job.jobId}</p>
                <div className="w-[1px] h-[14px] bg-[#C9C9C9]"></div>

                <p>
                  {job.experience?.length === 2
                    ? `${job.experience[0]}-${job.experience[1]} years of experience`
                    : `${job.experience?.join("-")} years of experience`}
                </p>
                <div className="w-[1px] h-[14px] bg-[#C9C9C9]"></div>

                <p>{job.location}</p>
              </div>
            </div>

            {/* RIGHT SIDE BUTTON — moves to next row on mobile */}
            {state === 1 && (
              <button
                onClick={() => setState(2)}
                className={`min-w-[130px] h-[40px] border border-black rounded-[16px] flex items-center justify-center text-[16px] font-nunito ml-auto md:ml-0`}
              >
                Apply
              </button>
            )}

            {state === 2 && (
              <button
                type="button"
                data-analytics-lead-submit
                onClick={handleSubmit}
                disabled={loading || !isEnabled}
                className={`
                  min-w-[256px]
                  hidden h-[48px]
                  text-white border bg-black border-black
                  rounded-[16px]
                  md:flex items-center justify-center
                  text-[14px] font-nunito ml-auto md:ml-0

                  ${
                    loading || !isEnabled
                      ? "opacity-40 cursor-not-allowed pointer-events-none"
                      : "cursor-pointer"
                  }
                `}
              >
                {loading ? "Submitting ..." : "Submit Application"}
              </button>
            )}

            {state === 3 && (
              <Link to="/careers">
                <p
                  className={`min-w-[256px] h-[48px] text-white border bg-black border-black rounded-[16px] flex items-center justify-center text-[14px] text-center font-nunito ml-auto md:ml-0 cursor-pointer`}
                >
                  Browse other jobs
                </p>
              </Link>
            )}
          </div>
          {state === 1 && (
            <div className="mb-10">
              <div className="p-[8px_16px] space-y-2">
                <ContentBlocks blocks={job.aboutRole.blocks} />
              </div>

              <div className="p-[8px_16px] space-y-2">
                <ContentBlocks blocks={job.jobRequirements.blocks} />
              </div>

              <div className="p-[8px_16px] space-y-2">
                <ContentBlocks blocks={job.additionalInfo.blocks} />
              </div>
            </div>
          )}

          {state === 2 && (
            <div className="flex md:flex-row flex-col p-[6px_16px] md:h-[555px] h-auto gap-[24px] pb-8">
              <div className="w-full md:flex-1 shrink-0">
                <h2 className="font-nunito font-bold text-[20px]">
                  Basic Details
                </h2>
                <form className="space-y-6 w-full max-w-[380px]">
                  {/* NAME */}
                  <div className="space-y-1">
                    <label className="font-nunito font-semibold text-[14px] text-black">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <div className="w-full h-[48px] rounded-[16px] border border-[#D9D9D9] flex items-center px-4 gap-3">
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your Name"
                        className="w-full outline-none font-nunito text-[14px] placeholder:text-[#A4A4A4]"
                      />
                    </div>
                  </div>

                  {/* EMAIL */}
                  <div className="space-y-1">
                    <label className="font-nunito font-semibold text-[14px] text-black">
                      Email ID <span className="text-red-500">*</span>
                    </label>

                    <div className="w-full h-[48px] rounded-[16px] border border-[#D9D9D9] flex items-center px-4 gap-3">
                      <img
                        src="/enevelopIcon.svg"
                        alt=""
                        className="w-4 h-4 opacity-70"
                      />
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="ex. user@domain.com"
                        className="w-full outline-none font-nunito text-[14px] placeholder:text-[#A4A4A4]"
                      />
                    </div>
                  </div>

                  {/* CONTACT NUMBER */}
                  <div className="space-y-1">
                    <label className="font-nunito font-semibold text-[14px] text-black">
                      Contact Number <span className="text-red-500">*</span>
                    </label>

                    <div className="w-full h-[48px] rounded-[16px] border border-[#D9D9D9] flex items-center px-4 gap-3">
                      <img
                        src="/phoneIcon.svg"
                        alt=""
                        className="w-4 h-4 opacity-70"
                      />
                      <span className="text-[#A4A4A4]">+91</span>
                      <input
                        type="number"
                        required
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="xxx-xxx-xxxx"
                        className="w-full outline-none font-nunito text-[14px] placeholder:text-[#A4A4A4]"
                      />
                    </div>
                  </div>

                  {/* CHECKBOX */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="terms"
                      required
                      checked={form.terms}
                      onChange={handleCheckbox}
                      className="custom-checkbox"
                      style={{
                        backgroundImage: "url('/checkedIcon.png')",
                      }}
                    />

                    <p className="font-nunito text-[14px] text-black">
                      I agree to the{" "}
                      <Link to="/terms" className="text-[#2C2C2C] font-bold">
                        Terms and Conditions
                      </Link>
                    </p>
                  </div>
                </form>
              </div>

              <div className="hidden md:block w-[1px] h-full bg-[#E0E0E0]"></div>

              <div className="w-full md:flex-1 shrink-0">
                {/* SECTION TITLE */}
                <h2 className="font-nunito font-bold text-[20px]">
                  Education and Experience
                </h2>

                <div className="space-y-2">
                  <p className="font-nunito font-semibold text-[16px]">
                    Educational Qualifications{" "}
                    <span className="text-red-500">*</span>
                  </p>

                  <div className="flex flex-wrap gap-3">
                    {["Higher Secondary", "Bachelors", "Masters", "PhD"].map(
                      renderQualificationBtn
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="font-nunito font-semibold text-[16px]">
                    Work Experience{" "}
                    <span className="text-[#808080]">(in years)</span>{" "}
                    <span className="text-red-500">*</span>
                  </p>

                  {/* Experience Input */}
                  <div className="w-full h-[48px] rounded-[16px] border border-[#D9D9D9] flex items-center px-4 gap-3">
                    <input
                      type="number"
                      name="workExp"
                      value={form.workExp}
                      onChange={handleChange}
                      disabled={form.isFresher}
                      placeholder="Years of experience"
                      className={`w-full outline-none font-nunito text-[14px] placeholder:text-[#A4A4A4] ${
                        form.isFresher ? "cursor-not-allowed" : ""
                      }`}
                    />
                  </div>

                  {/* Fresher Checkbox */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="isFresher"
                      checked={form.isFresher}
                      onChange={handleCheckbox}
                      className="custom-checkbox"
                      style={{
                        backgroundImage: "url('/checkedIcon.png')",
                      }}
                    />{" "}
                    <span className="font-nunito text-[14px]">
                      I'm a fresher
                    </span>
                  </label>
                </div>

                <div className="space-y-2">
                  <div
                    className={`grid gap-3 ${
                      form.isFresher ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"
                    }`}
                  >
                    {!form.isFresher && (
                      <div className="space-y-1">
                        <label className="font-nunito font-semibold text-[14px] text-black">
                          Current CTC{" "}
                          <span className="text-[#808080]"></span>{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="w-full h-[48px] rounded-[16px] border border-[#D9D9D9] flex items-center px-4 gap-3">
                          <input
                            type="number"
                            name="currentCtc"
                            value={form.currentCtc}
                            onChange={handleChange}
                            placeholder="e.g. 12 (in LPA)"
                            className="w-full outline-none font-nunito text-[14px] placeholder:text-[#A4A4A4]"
                          />
                        </div>
                      </div>
                    )}

                    <div className="space-y-1">
                      <label className="font-nunito font-semibold text-[14px] text-black">
                        Expected CTC{" "}
                        <span className="text-[#808080]"></span>{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="w-full h-[48px] rounded-[16px] border border-[#D9D9D9] flex items-center px-4 gap-3">
                        <input
                          type="number"
                          name="expectedCtc"
                          value={form.expectedCtc}
                          onChange={handleChange}
                          placeholder="e.g. 15 (in LPA)"
                          className="w-full outline-none font-nunito text-[14px] placeholder:text-[#A4A4A4]"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ======================== */}
                {/* NOTICE PERIOD */}
                {/* ======================== */}
                <div className="space-y-2">
                  <p className="font-nunito font-semibold text-[16px]">
                    Notice Period{" "}
                    <span className="text-[#808080]">(in days)</span>{" "}
                    <span className="text-red-500">*</span>
                  </p>

                  <div className="flex flex-wrap gap-3">
                    {["Immediate", "1 month", "2 months", "2+ months"].map(
                      renderNoticeBtn
                    )}
                  </div>
                </div>
              </div>

              <div className="hidden md:block w-[1px] h-full bg-[#E0E0E0]"></div>

              <div className="w-full md:flex-1 shrink-0">
                <h2 className="font-nunito font-bold text-[20px]">
                  Resume and Portfolio
                </h2>
                <div className="space-y-2">
                  <p className="font-nunito font-semibold text-[16px]">
                    Resume <span className="text-red-500">*</span>
                  </p>
                  {!form.resumeUrl && (
                    <div className="h-[174px] border-2 border-dashed gap-[16px] rounded-[20px] flex flex-col justify-between p-4">
                      <div className="flex flex-col items-center">
                        <img
                          src="/FilePdf.svg"
                          className="h-[48px] w-[48px]"
                          alt=""
                        />
                        <h3 className="font-nunito font-bold text-[16px] leading-[100%]">
                          Upload Resume
                        </h3>
                        <p className="text-[#525252] font-nunito font-bold text-[12px]">
                          Max file size: 8 MB
                        </p>
                      </div>
                      <div className="flex flex-col items-center gap-3">
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handleResumeUpload}
                          className="hidden"
                          id="resumeUpload"
                        />
                        <button
                          onClick={() =>
                            document.getElementById("resumeUpload").click()
                          }
                          disabled={form.resumeLoading}
                          className="w-full md:w-[304px] h-[48px] flex items-center justify-center gap-2 rounded-[16px] px-[12px] pr-[16px] bg-[#5FC18A] font-nunito font-medium text-[14px] leading-[100%] tracking-[-0.41px] text-white"
                        >
                          <img
                            src="/uploadIcon.svg"
                            className="w-[20px] h-[20px]"
                            alt=""
                          />
                          Upload
                        </button>
                      </div>
                    </div>
                  )}

                  {form.resumeLoading && (
                    <p className="text-center text-sm font-nunito">
                      Uploading...
                    </p>
                  )}

                  {form.resumeUrl && !form.resumeLoading && (
                    <div className="w-full bg-[#E5F7EB] border border-[#6FD8A0] rounded-[24px] p-4">
                      <div className="flex items-center justify-between">
                        <img
                          src="/selectedPdf.svg"
                          alt=""
                          className="w-6 h-6"
                        />

                        <button
                          onClick={reuploadResume}
                          className="flex items-center gap-2"
                        >
                          <img src="/reupload.svg" alt="" className="w-5 h-5" />{" "}
                          Reupload
                        </button>
                      </div>

                      <p className="font-nunito font-bold text-[16px] mt-3">
                        {form.resumeFile?.name}
                      </p>

                      <p className="font-nunito text-[14px] text-[#525252]">
                        File uploaded
                      </p>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="font-nunito font-semibold text-[16px]">
                    Portfolio
                  </p>
                  {!form.portfolioFileUrl && (
                    <div className="h-[276px] border-2 border-dashed rounded-[20px] flex flex-col justify-between p-4">
                      {/* TOP CONTENT */}
                      <div className="flex flex-col items-center">
                        <img
                          src="/FilePdf.svg"
                          className="h-[48px] w-[48px]"
                          alt=""
                        />
                        <h3 className="font-nunito font-bold text-[16px] leading-[100%]">
                          Upload Portfolio
                        </h3>
                        <p className="text-[#525252] font-nunito font-bold text-[12px]">
                          Max file size: 8 MB
                        </p>
                      </div>

                      {/* BOTTOM CONTENT (centered horizontally, bottom aligned) */}
                      <div className="flex flex-col items-center gap-3">
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handlePortfolioPDF}
                          className="hidden"
                          id="portfolioUpload"
                        />
                        <button
                          disabled={form.portfolioLoading}
                          onClick={() =>
                            document.getElementById("portfolioUpload").click()
                          }
                          className="w-full md:w-[304px] h-[48px] flex items-center justify-center gap-2 rounded-[16px] px-[12px] pr-[16px] bg-[#5FC18A] font-nunito font-medium text-[14px] leading-[100%] tracking-[-0.41px] text-white"
                        >
                          <img
                            src="/uploadIcon.svg"
                            className="w-[20px] h-[20px]"
                            alt=""
                          />
                          Upload
                        </button>

                        <div className="w-full md:w-[304px] h-[19px] flex justify-center items-center gap-2 px-2">
                          <div className="w-[89px] border-dashed border-[#999999] [border-top-style:dashed] [border-top-width:1px] [stroke-dasharray:2_2]"></div>
                          <p className="font-nunito font-medium text-[14px] text-[#999999]">
                            OR
                          </p>
                          <div className="w-[89px] border-dashed border-[#999999] [border-top-style:dashed] [border-top-width:1px] [stroke-dasharray:2_2]"></div>
                        </div>

                        <div className="w-full md:w-[304px] h-[48px] rounded-[16px] border border-[#D9D9D9] flex items-center px-4 gap-3">
                          <img
                            src="/Link.svg"
                            alt=""
                            className="w-4 h-4 opacity-70"
                          />
                          <input
                            type="text"
                            value={form.portfolioLink}
                            onChange={handlePortfolioLink}
                            placeholder="Add a portfolio link"
                            className="w-full outline-none font-nunito text-[14px] placeholder:text-[#A4A4A4]"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {form.portfolioLoading && (
                    <p className="text-center text-sm font-nunito">
                      Uploading...
                    </p>
                  )}

                  {form.portfolioFileUrl && !form.portfolioLoading && (
                    <div className="w-full bg-[#E5F7EB] border border-[#6FD8A0] rounded-[24px] p-4">
                      <div className="flex items-center justify-between">
                        <img
                          src="/selectedPdf.svg"
                          alt=""
                          className="w-6 h-6"
                        />
                        <button
                          onClick={reuploadPortfolio}
                          className="flex items-center gap-2"
                        >
                          <img src="/reupload.svg" alt="" className="w-5 h-5" />{" "}
                          Reupload
                        </button>
                      </div>

                      <p className="font-nunito font-bold text-[16px] mt-3">
                        {form.portfolioFile?.name}
                      </p>
                    </div>
                  )}

                  {/* Entered Link */}
                  {form.portfolioLink && !form.portfolioFileUrl && (
                    <div className="w-full max-w-[420px] h-[46px] flex items-center justify-between mt-3">
                      <div className="w-fit h-[46px] flex items-center gap-2 rounded-[12px] border border-[#5FC18A] bg-[#E2F4E9] px-2">
                        <img
                          src="/ArrowUpRight.svg"
                          alt=""
                          className="w-4 h-4"
                        />
                        <div className="flex flex-col">
                          <p className="font-nunito font-semibold text-[12px] ">
                            Portfolio
                          </p>
                          <p className="font-nunito text-[10px] w-full truncate text-[#525252]">
                            {form.portfolioLink}
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={reuploadPortfolio}
                        className="flex items-center gap-2"
                      >
                        <img src="/NotePencil.svg" alt="" className="w-4 h-4" />{" "}
                        Change
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {state === 2 && (
                <div className="md:hidden block w-full shrink-0 border-t border-[#E0E0E0] px-4 py-4 mt-6">
                  <div className="flex flex-col items-center gap-3">
                    <button
                      type="button"
                      data-analytics-lead-submit
                      onClick={handleSubmit}
                      disabled={loading || !isEnabled}
                      className={`
                        w-full h-[48px]
                        flex items-center justify-center gap-[8px]
                        rounded-[16px] border border-[#D9D9D9]
                        bg-black text-[#FFFFFF]
                        font-nunito font-semibold text-[16px] leading-[100%] tracking-[-0.41px]
                        ${
                          loading || !isEnabled
                            ? "opacity-40 cursor-not-allowed pointer-events-none"
                            : "cursor-pointer"
                        }
                      `}
                    >
                      {loading ? "Submiting ..." : "Submit Application"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {state === 3 && (
            <div className="min-h-[555px] mb-10 w-full flex flex-col md:flex-row items-center justify-center px-6 gap-10">
              <div className="flex justify-center md:justify-end w-full md:w-auto">
                <img
                  src="/success.png"
                  alt="success"
                  className="w-full h-full md:w-[400px] md:h-[400px]"
                />
              </div>

              <div className="flex flex-col items-start md:items-start text-black max-w-[400px]">
                <h2 className="font-nunito font-bold text-[40px] leading-none tracking-[-2%] text-center md:text-left">
                  Application sent.
                </h2>

                <p className="mt-4 font-nunito font-semibold text-[14px] leading-tight text-[#656565] md:text-left ">
                  You have successfully applied to{" "}
                  <span className="font-bold">{job.title}</span>. Apply to other
                  similar jobs for a greater chance of recruitment.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <NewFooter inFlow />
    </div>
  );
}

export default JobDetail;
